import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Calender.css';
import Nabar1 from '../Navbar/Navbar1';
import Mainbar from '../Navbar/Mainbar';

// Backend URL determination - Same logic as Login.jsx
const getBackendUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  if (process.env.NODE_ENV === 'production') {
    return 'https://api.leave-management.manurevasolutions.com';
  }
  
  return 'http://localhost:5000';
};

const BACKEND_URL = getBackendUrl();

const ManagerCalendar = () => {
  const navigate = useNavigate();
  const [currentDate] = useState(new Date());
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedDateLeaves, setSelectedDateLeaves] = useState([]);
  const [selectedDateStr, setSelectedDateStr] = useState('');
  const [userData, setUserData] = useState({
    name: 'Loading...',
    role: 'Loading...',
    email: '',
    picture: ''
  });

  // Fetch current user data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/user`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          setUserData({
            name: data.name || 'User',
            role: data.role || 'Employee',
            email: data.email || '',
            picture: data.picture || data.profile_picture || ''
          });
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, []);

  // Fetch assigned employees' upcoming leave requests
  useEffect(() => {
    const fetchUpcomingLeaves = async () => {
      console.log('📄 Starting to fetch upcoming leaves...');
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/manager/upcoming-leaves`, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('✅ Raw API Response:', data);
        
        if (!Array.isArray(data)) {
          setLeaveRequests([]);
          setLoading(false);
          return;
        }
        
        // Filter for UPCOMING dates only (next 30 days from today)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const thirtyDaysFromNow = new Date(today);
        thirtyDaysFromNow.setDate(today.getDate() + 30);
        
        const filteredLeaves = data.filter(leave => {
          try {
            const startDate = new Date(leave.startDateISO || leave.startDate || leave.start_date);
            startDate.setHours(0, 0, 0, 0);
            return startDate >= today && startDate <= thirtyDaysFromNow;
          } catch (err) {
            return false;
          }
        });
        
        // Store original leaves with full information
        const formattedLeaves = filteredLeaves.map(leave => {
          const startDate = new Date(leave.startDateISO || leave.startDate || leave.start_date);
          const endDate = new Date(leave.endDateISO || leave.endDate || leave.end_date);
          const leaveType = leave.leaveType || leave.leave_type || 'Casual';
          const leaveTypeLower = leaveType.toLowerCase();
          
          let normalizedType = 'Casual';
          let color = '#E3F2FD';
          let textColor = '#1976D2';
          
          if (leaveTypeLower.includes('medical') || leaveTypeLower.includes('sick')) {
            normalizedType = 'Medical';
            color = '#FFEBEE';
            textColor = '#D32F2F';
          } else if (leaveTypeLower.includes('permission') || leaveTypeLower.includes('emergency')) {
            normalizedType = 'Permission';
            color = '#FFF9C4';
            textColor = '#F57F17';
          }
          
          return {
            type: normalizedType,
            employeeName: leave.employeeName || leave.employee_name || 'Unknown',
            employeeEmail: leave.employeeEmail || leave.employee_email || '',
            reason: leave.reason || 'No reason provided',
            startDate: startDate,
            endDate: endDate,
            startDateISO: startDate.toISOString(),
            endDateISO: endDate.toISOString(),
            color: color,
            textColor: textColor,
            isHoliday: false,
            date: startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            sortDate: startDate
          };
        });
        
        setLeaveRequests(formattedLeaves);
        
      } catch (error) {
        console.error('❌ Error fetching upcoming leaves:', error);
        setError(error.message);
        setLeaveRequests([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUpcomingLeaves();
  }, [currentDate]);

  // Fetch government holidays
  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/holidays`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          const thirtyDaysFromNow = new Date(today);
          thirtyDaysFromNow.setDate(today.getDate() + 30);
          
          const upcomingHolidays = data.filter(holiday => {
            const holidayDate = new Date(holiday.date);
            holidayDate.setHours(0, 0, 0, 0);
            return holidayDate >= today && holidayDate <= thirtyDaysFromNow;
          });
          
          setHolidays(upcomingHolidays);
          
          const holidayCards = upcomingHolidays.map(holiday => {
            const holidayDate = new Date(holiday.date);
            return {
              type: 'Public Holiday',
              date: holidayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
              color: '#FFFFFF',
              textColor: '#333333',
              employeeName: holiday.reason || 'Public Holiday',
              reason: holiday.reason || 'Public Holiday',
              sortDate: holidayDate,
              isHoliday: true,
              holidayReason: holiday.reason,
              startDate: holidayDate,
              endDate: holidayDate,
              startDateISO: holidayDate.toISOString(),
              endDateISO: holidayDate.toISOString()
            };
          });
          
          setLeaveRequests(prevRequests => {
            const combined = [...prevRequests, ...holidayCards];
            combined.sort((a, b) => a.sortDate - b.sortDate);
            return combined;
          });
        }
      } catch (error) {
        console.error('Error fetching holidays:', error);
      }
    };

    if (!loading) {
      fetchHolidays();
    }
  }, [loading]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    return { firstDay, daysInMonth };
  };

  // ✅ NEW: Get ALL leaves for a specific date
  const getLeavesForDate = (day) => {
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    const checkDate = new Date(currentYear, currentMonth, day);
    checkDate.setHours(0, 0, 0, 0);
    
    // Filter all leaves that include this date
    const leavesOnThisDay = leaveRequests.filter(leave => {
      try {
        const leaveStart = new Date(leave.startDate);
        const leaveEnd = new Date(leave.endDate);
        
        leaveStart.setHours(0, 0, 0, 0);
        leaveEnd.setHours(0, 0, 0, 0);
        
        return checkDate >= leaveStart && checkDate <= leaveEnd;
      } catch (error) {
        return false;
      }
    });
    
    return leavesOnThisDay;
  };

  // ✅ NEW: Handle day click to show modal
  const handleDayClick = (day) => {
    const leaves = getLeavesForDate(day);
    
    if (leaves.length === 0) {
      return; // No leaves, do nothing
    }
    
    const dateObj = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const formattedDate = dateObj.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    
    setSelectedDateStr(formattedDate);
    setSelectedDateLeaves(leaves);
    setShowModal(true);
  };

  // ✅ Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedDateLeaves([]);
    setSelectedDateStr('');
  };

  const renderCalendarDays = () => {
    const { firstDay, daysInMonth } = getDaysInMonth(currentDate);
    const days = [];
    const today = currentDate.getDate();
    const todayDate = new Date();
    const isCurrentMonth = currentDate.getMonth() === todayDate.getMonth() && 
                          currentDate.getFullYear() === todayDate.getFullYear();
    
    // Previous month days (grayed out)
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="manager-calendar-day manager-calendar-day-empty"></div>);
    }
    
    // Current month days with multi-leave support
    for (let day = 1; day <= daysInMonth; day++) {
      const isToday = isCurrentMonth && day === today;
      const leaves = getLeavesForDate(day);
      
      let dayClasses = 'manager-calendar-day';
      let dayTitle = '';
      let dayStyle = {};
      
      if (leaves.length === 0) {
        // No leaves - working day
        if (isToday) {
          dayClasses += ' manager-calendar-day-today-working';
          dayTitle = 'Today - Working Day';
        }
      } else if (leaves.length === 1) {
        // Single leave
        const leave = leaves[0];
        const typeClass = leave.type.toLowerCase().replace(' ', '-');
        
        if (isToday) {
          dayClasses += ` manager-calendar-day-today-${typeClass}`;
          if (leave.isHoliday) {
            dayTitle = `Today - ${leave.holidayReason}`;
          } else {
            dayTitle = `Today - ${leave.type} Leave (${leave.employeeName})`;
          }
        } else {
          if (leave.isHoliday) {
            dayClasses += ' manager-calendar-day-holiday';
            dayTitle = leave.holidayReason;
          } else {
            dayClasses += ` manager-calendar-day-${typeClass}`;
            dayTitle = `${leave.type} Leave - ${leave.employeeName}`;
          }
        }
      } else {
        // ✅ MULTIPLE LEAVES - Split colors
        dayClasses += ' manager-calendar-day-multi-leave';
        
        // Build title with all employee names
        dayTitle = `${leaves.length} Leaves:\n${leaves.map(l => `${l.type} - ${l.employeeName}`).join('\n')}`;
        
        // Create gradient colors
        const colors = leaves.map(leave => {
          if (leave.type === 'Casual' || leave.type === 'Casual Leave') return '#2196F3';
          if (leave.type === 'Medical' || leave.type === 'Medical Leave') return '#FF0004';
          if (leave.type === 'Permission') return '#A78602';
          if (leave.isHoliday) return '#9C27B0';
          return '#2196F3';
        });
        
        // Split color logic
        if (leaves.length === 2) {
          // 50/50 vertical split
          dayStyle = {
            background: `linear-gradient(90deg, ${colors[0]} 50%, ${colors[1]} 50%)`,
            color: 'white',
            fontWeight: '700'
          };
        } else if (leaves.length === 3) {
          // Three colors split
          dayStyle = {
            background: `linear-gradient(90deg, ${colors[0]} 33.33%, ${colors[1]} 33.33%, ${colors[1]} 66.66%, ${colors[2]} 66.66%)`,
            color: 'white',
            fontWeight: '700'
          };
        } else {
          // 4+ leaves - show first 3 colors
          dayStyle = {
            background: `linear-gradient(90deg, ${colors[0]} 33.33%, ${colors[1]} 33.33%, ${colors[1]} 66.66%, ${colors[2]} 66.66%)`,
            color: 'white',
            fontWeight: '700'
          };
        }
        
        // Add golden ring for today
        if (isToday) {
          dayStyle.boxShadow = '0 0 0 3px #FFD700';
          dayStyle.borderRadius = '50%';
        }
      }
      
      days.push(
        <div 
          key={day} 
          className={dayClasses} 
          title={dayTitle}
          style={dayStyle}
          onClick={() => handleDayClick(day)}
        >
          {day}
          {leaves.length > 3 && (
            <span className="leave-count-badge">
              {leaves.length}
            </span>
          )}
        </div>
      );
    }
    
    // Next month days (grayed out)
    const totalCells = Math.ceil((firstDay + daysInMonth) / 7) * 7;
    const remainingCells = totalCells - (firstDay + daysInMonth);
    for (let i = 1; i <= remainingCells; i++) {
      days.push(
        <div key={`next-${i}`} className="manager-calendar-day manager-calendar-day-next">
          {i}
        </div>
      );
    }
    
    return days;
  };

  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const currentMonthName = monthNames[currentDate.getMonth()];
  const currentYear = currentDate.getFullYear();
  const currentDay = currentDate.getDate();
  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const currentDayName = dayNames[currentDate.getDay()];

  return (
    <div className="manager-calendar-container" style={{display:'flex'}} >
      <div className="manager-calendar-navbar"><Nabar1/></div>

      <div className="manager-calendar-main"  >
        <div className="manager-calender-first" style={{width:'100%',}} >
          <div className="manager-calender-haeder"><Mainbar/></div>
          
          <main className="manager-calendar-content" >
            <div className="manager-calendar-title-section">
              <div>
                <h2 className="manager-calendar-title">Team Calendar</h2>
                <p className="manager-calendar-subtitle">Manage your team leave calendar</p>
              </div>
              <div className="manager-calendar-actions">
                <div className="manager-calendar-search">
                  <svg className="manager-calendar-search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="white" strokeWidth="2"/>
                    <path d="M14 14L11.1 11.1" stroke="white" strokeWidth="2"/>
                  </svg>
                  <input type="text" placeholder="Search for..." className="manager-calendar-search-input" />
                </div>
                <button className="manager-calendar-refresh-btn" onClick={() => window.location.reload()}>
                  <svg className="manager-calendar-refresh-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M1.33334 2.66667V6.66667H5.33334" stroke="white" strokeWidth="2"/>
                    <path d="M14.6667 13.3333V9.33333H10.6667" stroke="white" strokeWidth="2"/>
                    <path d="M13.2867 6C13.0185 5.11785 12.5459 4.31238 11.9065 3.64849C11.267 2.98459 10.4785 2.48219 9.60675 2.18311C8.73499 1.88403 7.80605 1.79663 6.89362 1.92814C5.98119 2.05965 5.11126 2.40635 4.36001 2.94001L1.33334 5.33334M14.6667 10.6667L11.64 13.06C10.8888 13.5937 10.0188 13.9404 9.10641 14.0719C8.19398 14.2034 7.26504 14.116 6.39328 13.8169C5.52152 13.5178 4.73304 13.0154 4.09358 12.3515C3.45413 11.6876 2.98155 10.8822 2.71334 10" stroke="white" strokeWidth="2"/>
                  </svg>
                  Refresh
                </button>
              </div>
            </div>

            <div className="manager-calendar-second-row">
              <div className="manager-calendar-month-header">
                <h3 className="manager-calendar-month-title">{currentMonthName} {currentYear}</h3>
                <div className="manager-calendar-legend">
                  <span className="manager-calendar-legend-item" style={{color:'#2196F3'}}>
                    <span className="manager-calendar-legend-dot manager-calendar-legend-casual"></span> Casual
                  </span>
                  <span className="manager-calendar-legend-item">
                    <span className="manager-calendar-legend-dot manager-calendar-legend-medical" style={{color:'#FF0004'}}></span> Medical
                  </span>
                  <span className="manager-calendar-legend-item">
                    <span className="manager-calendar-legend-dot manager-calendar-legend-permission"></span> Permission
                  </span>
                  <span className="manager-calendar-legend-item">
                    <span className="manager-calendar-legend-dot" style={{backgroundColor: '#848484'}}></span> Annual
                  </span>
                  <span className="manager-calendar-legend-item">
                    <span className="manager-calendar-legend-dot" style={{backgroundColor: '#4CAF50'}}></span> Working
                  </span>
                </div>
              </div>

              <div className="manager-calendar-grid-container">
                <div className="manager-calendar-current-day">
                  <div className="manager-calendar-current-month">{currentMonthName}</div>
                  <div className="manager-calendar-current-day-number">{String(currentDay).padStart(2, '0')}</div>
                  <div className="manager-calendar-current-day-name">{currentDayName}</div>
                  <div className="manager-calendar-current-year">{currentYear}</div>
                </div>

                <div className="manager-calendar-grid-wrapper">
                  <div className="manager-calendar-weekdays">
                    <div className="manager-calendar-weekday">S</div>
                    <div className="manager-calendar-weekday">M</div>
                    <div className="manager-calendar-weekday">T</div>
                    <div className="manager-calendar-weekday">W</div>
                    <div className="manager-calendar-weekday">T</div>
                    <div className="manager-calendar-weekday">F</div>
                    <div className="manager-calendar-weekday">S</div>
                  </div>
                  <div className="manager-calendar-days">
                    {renderCalendarDays()}
                  </div>
                </div>
              </div>

              <div className="manager-calendar-upcoming">
                <h3 className="manager-calendar-upcoming-title">Upcoming Events</h3>
                <div className="manager-calendar-events-summary">
                  <div className="manager-calendar-event-count">
                    <span className="manager-calendar-event-label">Events</span>
                    <span className="manager-calendar-event-number">0</span>
                  </div>
                  <div className="manager-calendar-event-count">
                    <span className="manager-calendar-event-label">Festival</span>
                    <span className="manager-calendar-event-number">{loading ? '...' : holidays.length}</span>
                  </div>
                </div>
              </div>

              <div className="manager-calendar-leave-requests">
                <h3 className="manager-calendar-leave-title">Upcoming Leave Requests (Next 30 Days)</h3>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    Loading leave requests...
                  </div>
                ) : error ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#d32f2f' }}>
                    Error: {error}
                  </div>
                ) : leaveRequests.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    No upcoming leave requests for the next 30 days
                  </div>
                ) : (
                  <div className="manager-calendar-leave-grid">
                    <div className="leave-type-section">
                      {leaveRequests.filter(request => request.type === 'Casual').map((request, index) => (
                        <div key={`casual-${index}`} className="manager-calendar-leave-card" style={{ backgroundColor: '#E3F2FD' }}>
                          <div className="manager-calendar-leave-type manager-calendar-leave-type-casual">{request.type}</div>
                          <div className="manager-calendar-leave-date">{request.date}</div>
                          <div style={{fontSize: '11px', color: '#666', marginTop: '4px'}}>{request.employeeName}</div>
                        </div>
                      ))}
                    </div>

                    <div className="leave-type-section">
                      {leaveRequests.filter(request => request.type === 'Medical').map((request, index) => (
                        <div key={`medical-${index}`} className="manager-calendar-leave-card" style={{ backgroundColor: '#FFEBEE' }}>
                          <div className="manager-calendar-leave-type manager-calendar-leave-type-medical">{request.type}</div>
                          <div className="manager-calendar-leave-date">{request.date}</div>
                          <div style={{fontSize: '11px', color: '#666', marginTop: '4px'}}>{request.employeeName}</div>
                        </div>
                      ))}
                    </div>

                    <div className="leave-type-section">
                      {leaveRequests.filter(request => request.type === 'Permission' || request.isHoliday).map((request, index) => (
                        <div 
                          key={`other-${index}`} 
                          className="manager-calendar-leave-card"
                          style={{ 
                            backgroundColor: request.isHoliday ? '#eeececff' : '#FFF9C4',
                            border: request.isHoliday ? '2px solid #E0E0E0' : 'none'
                          }}
                        >
                          <div className="manager-calendar-leave-type" style={{color: request.isHoliday ? '#585858ff' : '#F57F17'}}>
                            {request.isHoliday ? '🎉 Holiday' : request.type}
                          </div>
                          <div className="manager-calendar-leave-date">{request.date}</div>
                          {!request.isHoliday && (
                            <div style={{fontSize: '11px', color: '#666', marginTop: '4px'}}>{request.employeeName}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* ✅ NEW: Modal for Leave Details */}
      {showModal && (
        <div className="leave-modal-overlay" onClick={closeModal}>
          <div className="leave-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="leave-modal-header">
              <span>📅 Leave Details</span>
              <button className="leave-modal-close" onClick={closeModal}>×</button>
            </div>
            
            <div className="leave-modal-subtitle">
              <strong>{selectedDateStr}</strong> - {selectedDateLeaves.length} Leave{selectedDateLeaves.length > 1 ? 's' : ''}
            </div>

            {selectedDateLeaves.map((leave, index) => (
              <div 
                key={index} 
                className={`leave-detail-card ${leave.type.toLowerCase().replace(' ', '-')}`}
              >
                <div className="leave-detail-header">
                  {index + 1}. {leave.type} Leave
                  <span className={`leave-type-badge ${leave.type.toLowerCase()}`}>
                    {leave.type}
                  </span>
                </div>

                {!leave.isHoliday && (
                  <>
                    <div className="leave-detail-row">
                      <span className="leave-detail-label">Employee:</span>
                      <span className="leave-detail-value">{leave.employeeName}</span>
                    </div>

                    {leave.employeeEmail && (
                      <div className="leave-detail-row">
                        <span className="leave-detail-label">Email:</span>
                        <span className="leave-detail-value">{leave.employeeEmail}</span>
                      </div>
                    )}

                    <div className="leave-detail-row">
                      <span className="leave-detail-label">Reason:</span>
                      <span className="leave-detail-value">{leave.reason}</span>
                    </div>

                    <div className="leave-detail-row">
                      <span className="leave-detail-label">Duration:</span>
                      <span className="leave-detail-value">
                        {leave.startDate.toLocaleDateString()} 
                        {leave.startDate.toDateString() !== leave.endDate.toDateString() && 
                          ` - ${leave.endDate.toLocaleDateString()}`}
                      </span>
                    </div>
                  </>
                )}

                {leave.isHoliday && (
                  <div className="leave-detail-row">
                    <span className="leave-detail-label">Holiday:</span>
                    <span className="leave-detail-value">{leave.holidayReason}</span>
                  </div>
                )}
              </div>
            ))}

            <div className="leave-modal-footer">
              <button className="modal-close-btn" onClick={closeModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Debug Info (only in development) */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{
          position: 'fixed',
          bottom: '10px',
          right: '10px',
          backgroundColor: '#f3f4f6',
          padding: '8px 12px',
          borderRadius: '6px',
          fontSize: '11px',
          color: '#6b7280',
          maxWidth: '300px',
          border: '1px solid #e5e7eb',
          zIndex: 9999
        }}>
          <strong>Debug Info:</strong><br/>
          Backend: {BACKEND_URL}<br/>
          Environment: {process.env.NODE_ENV}<br/>
          Loading: {loading ? 'Yes' : 'No'}<br/>
          Leaves Count: {leaveRequests.length}<br/>
          Holidays Count: {holidays.length}
        </div>
      )}
    </div>
  );
};

export default ManagerCalendar;